import React, { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import useSelectTemplate from '../hooks/useSelectTemplate';
import usePrevious from '../../../utils/react/usePrevious';

import { templatePickerVisibleSelector, templatesSelector } from '../boardTemplatePickerSelectors';

import { NavigationDetail, NavigationLevel, TemplatesMap } from '../templateTypes';

type BoardTemplatePickerContextState = {
    templatePickerVisible: boolean;

    templates: TemplatesMap;
    templateFetchErrors: Record<string, string>;

    selectedTemplateId: string | null;
    loadingTemplateId: string | null;
    selectTemplate: (templateId: string | null) => Promise<void>;

    navigation: NavigationDetail;
    setNavigation: (navigation: NavigationDetail) => void;
    navigationTransition: string;

    isCopyingTemplate: boolean;
    setIsCopyingTemplate: (isCopyingTemplate: boolean) => void;

    keepTemplateContentChecked: boolean;
    setKeepTemplateContentChecked: (keepTemplateContentChecked: boolean) => void;

    categoriesLastScrollTopRef: React.MutableRefObject<number>;
    prevSelectedTemplateIdRef: React.MutableRefObject<string | null>;
};

export const boardTemplatePickerContext = createContext<BoardTemplatePickerContextState>({
    templatePickerVisible: false,

    templates: {},
    templateFetchErrors: {},

    selectedTemplateId: null,
    loadingTemplateId: null,
    selectTemplate: async () => {},

    navigation: { level: NavigationLevel.DEFAULT },
    setNavigation: () => {},
    navigationTransition: '',

    isCopyingTemplate: false,
    setIsCopyingTemplate: () => {},

    keepTemplateContentChecked: false,
    setKeepTemplateContentChecked: () => {},

    categoriesLastScrollTopRef: { current: 0 },
    prevSelectedTemplateIdRef: { current: null },
});

type Props = {
    children: ReactNode;
    enabled?: boolean;
};

export const BoardTemplatePickerContextProvider = ({ children, enabled = true }: Props) => {
    const templatePickerVisible = useSelector(templatePickerVisibleSelector);

    const [navigation, setNavigation] = useState<NavigationDetail>({ level: NavigationLevel.DEFAULT });
    const lastNavigationLevel = usePrevious(navigation);
    const navigationTransition =
        lastNavigationLevel && lastNavigationLevel.level > navigation.level ? 'slide-right' : 'slide-left';

    const [isCopyingTemplate, setIsCopyingTemplate] = useState(false);
    const [keepTemplateContentChecked, setKeepTemplateContentChecked] = useState(false);

    const templates = useSelector(templatesSelector);
    const { selectedTemplateId, selectTemplate, loadingTemplateId, templateFetchErrors } = useSelectTemplate({
        templates,
        enabled,
    });

    const categoriesLastScrollTopRef = useRef(0);
    const prevSelectedTemplateIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (selectedTemplateId) {
            prevSelectedTemplateIdRef.current = selectedTemplateId;
        }
    }, [selectedTemplateId]);

    useEffect(() => {
        prevSelectedTemplateIdRef.current = null;

        if (selectedTemplateId) selectTemplate(null);
    }, [navigation]);

    const value = {
        templatePickerVisible,

        templates,
        templateFetchErrors,

        selectedTemplateId,
        selectTemplate,
        loadingTemplateId,

        navigation,
        setNavigation,
        navigationTransition,

        isCopyingTemplate,
        setIsCopyingTemplate,

        keepTemplateContentChecked,
        setKeepTemplateContentChecked,

        categoriesLastScrollTopRef,
        prevSelectedTemplateIdRef,
    };

    return <boardTemplatePickerContext.Provider value={value}>{children}</boardTemplatePickerContext.Provider>;
};
